<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import WorkingHoursEditor from './components/WorkingHoursEditor.vue';
import AutomationConfigurationCards from './components/AutomationConfigurationCards.vue';
import api from 'dashboard/api/aceleres/iaRodizios';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  settings: { type: Object, required: true },
  isSaving: { type: Boolean, default: false },
  teamId: { type: [Number, String], required: true },
});
const emit = defineEmits(['save']);

const rodizioEnabled = ref(props.settings.rodizio_enabled);
const idleMinutes = ref(props.settings.rodizio_idle_minutes);
const maxCycles = ref(props.settings.rodizio_max_cycles);
const managerId = ref(props.settings.rodizio_manager_user_id);
const horarioMode = ref(props.settings.rodizio_horario_mode || 'global');
const globalWorkingHours = ref([...(props.settings.global_working_hours || [])]);

// Mantém refs locais em sync com props.settings quando o pai recarrega do servidor.
watch(() => props.settings, s => {
  rodizioEnabled.value = s.rodizio_enabled;
  idleMinutes.value = s.rodizio_idle_minutes;
  maxCycles.value = s.rodizio_max_cycles;
  managerId.value = s.rodizio_manager_user_id;
  horarioMode.value = s.rodizio_horario_mode || 'global';
  globalWorkingHours.value = [...(s.global_working_hours || [])];
}, { deep: true });

const managers = computed(() => props.settings.managers || []);

const onToggle = v => {
  if (v && !managerId.value) {
    useAlert('Selecione um gestor antes de ativar o rodízio');
    return;
  }

  emit('save', {
    rodizio_enabled: v,
    ...(v ? { rodizio_manager_user_id: managerId.value } : {}),
  });
};

const saveWindowConfig = () => {
  if (rodizioEnabled.value && !managerId.value) {
    useAlert('Selecione um gestor antes de salvar os parâmetros');
    return;
  }

  emit('save', {
    rodizio_idle_minutes: Math.max(1, Number(idleMinutes.value) || 4),
    rodizio_max_cycles: Math.max(1, Number(maxCycles.value) || 1),
    rodizio_manager_user_id: managerId.value || null,
  });
};

const saveGlobalHours = () => emit('save', { global_working_hours: globalWorkingHours.value });
const saveHorarioMode = () => emit('save', { rodizio_horario_mode: horarioMode.value });

// --------- Per-agent working hours ---------
const agents = ref([]);
const isLoadingAgents = ref(false);
const selectedAgent = ref(null);
const editingAgentHours = ref([]);

const fetchAgents = async () => {
  isLoadingAgents.value = true;
  try {
    const { data } = await api.listAgents(props.teamId);
    agents.value = data.data || [];
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Erro ao carregar vendedores');
  } finally {
    isLoadingAgents.value = false;
  }
};

const editAgentSchedule = agent => {
  selectedAgent.value = agent;
  // Se o agente não tem horário próprio, inicia com cópia do global
  const base = agent.configured && agent.working_hours.length === 7
    ? agent.working_hours
    : props.settings.global_working_hours;
  editingAgentHours.value = base.map(x => ({ ...x }));
};

const saveAgentSchedule = async () => {
  if (!selectedAgent.value) return;
  try {
    await api.updateAgentWorkingHours(
      selectedAgent.value.id,
      editingAgentHours.value,
      props.teamId,
    );
    useAlert(`Horário salvo para ${selectedAgent.value.name}`);
    selectedAgent.value = null;
    await fetchAgents();
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Erro ao salvar horário');
  }
};

const resetAgentSchedule = async agent => {
  if (!confirm(`Remover horário individual de ${agent.name}? (passará a usar o horário geral)`)) return;
  try {
    await api.resetAgentWorkingHours(agent.id, props.teamId);
    useAlert(`Horário individual removido para ${agent.name}`);
    if (selectedAgent.value?.id === agent.id) selectedAgent.value = null;
    fetchAgents();
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Erro ao resetar horário');
  }
};

onMounted(fetchAgents);
</script>

<template>
  <div class="flex flex-col gap-6 w-full">
    <AutomationConfigurationCards type="rodizio" :team-id="props.teamId" />

    <div v-if="false" class="flex flex-col gap-6">
    <!-- Toggle Rodízio -->
    <section class="border border-n-strong bg-n-background rounded-lg p-5 flex items-center justify-between gap-4">
      <div>
        <h2 class="text-body-large text-n-slate-12 font-medium m-0">Rodízio automático</h2>
        <p class="text-body-small text-n-slate-11 m-0">
          Quando ativo, leads sem resposta humana são transferidos automaticamente entre vendedores
          durante o horário de expediente. Independente do toggle de IA.
        </p>
      </div>
      <Switch :model-value="rodizioEnabled" @update:model-value="onToggle" />
    </section>

    <!-- Parâmetros dentro do expediente -->
    <section class="border border-n-strong bg-n-background rounded-lg p-5 flex flex-col gap-4">
      <header>
        <h2 class="text-body-large text-n-slate-12 font-medium m-0">Rodízio durante o expediente</h2>
        <p class="text-body-small text-n-slate-11 m-0">
          Como o sistema redistribui leads quando estamos dentro do horário comercial.
        </p>
      </header>
      <div class="grid grid-cols-3 gap-4">
        <div class="flex flex-col gap-1.5">
          <label class="text-body-small text-n-slate-12 font-medium">Tempo entre redistribuições</label>
          <div class="flex items-center gap-2">
            <input v-model.number="idleMinutes" type="number" min="1" max="60"
              class="w-20 border border-n-strong rounded-md py-2 px-3 bg-n-background text-n-slate-12" />
            <span class="text-body-small text-n-slate-11">minutos</span>
          </div>
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-body-small text-n-slate-12 font-medium">Ciclos antes do primeiro alerta</label>
          <input v-model.number="maxCycles" type="number" min="1" max="20"
            class="w-20 border border-n-strong rounded-md py-2 px-3 bg-n-background text-n-slate-12" />
          <p class="text-body-small text-n-slate-11 m-0">
            Cada ciclo passa por todos os vendedores elegíveis. Ao atingir esse número, o gestor é avisado ao fim de cada novo ciclo sem atendimento, e o rodízio continua normalmente.
          </p>
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-body-small text-n-slate-12 font-medium">Gestor a ser notificado</label>
          <select v-model="managerId"
            class="border border-n-strong rounded-md py-2 px-3 bg-n-background text-n-slate-12">
            <option :value="null">
              Selecione um gestor
            </option>
            <option v-for="m in managers" :key="m.id" :value="m.id">{{ m.name }}</option>
          </select>
        </div>
      </div>
      <div class="flex justify-end">
        <Button label="Salvar parâmetros" :is-loading="isSaving" @click="saveWindowConfig" />
      </div>
    </section>

    <!-- Comportamento fora do expediente (read-only / informativo) -->
    <section class="border border-n-strong bg-n-background rounded-lg p-5">
      <h2 class="text-body-large text-n-slate-12 font-medium m-0">Fora do expediente</h2>
      <ul class="text-body-small text-n-slate-11 mt-2 list-disc list-inside space-y-1">
        <li>O lead continua sendo atribuído a um vendedor da fila.</li>
        <li>A IA assume o atendimento, se estiver habilitada.</li>
        <li>O rodízio automático fica desativado nesse período (não há redistribuição).</li>
      </ul>
    </section>

    <!-- Horários -->
    <section class="border border-n-strong bg-n-background rounded-lg p-5 flex flex-col gap-4">
      <header>
        <h2 class="text-body-large text-n-slate-12 font-medium m-0">Horários do rodízio</h2>
        <p class="text-body-small text-n-slate-11 m-0">
          Escolha entre um horário único para todo o time ou horários individuais por vendedor.
        </p>
      </header>
      <div class="flex items-center gap-6">
        <label class="flex items-center gap-2 text-body-medium text-n-slate-12">
          <input v-model="horarioMode" type="radio" value="global" @change="saveHorarioMode" />
          Horário geral (mesmo para todos)
        </label>
        <label class="flex items-center gap-2 text-body-medium text-n-slate-12">
          <input v-model="horarioMode" type="radio" value="per_agent" @change="saveHorarioMode" />
          Horário por vendedor
        </label>
      </div>

      <!-- Modo global -->
      <div v-if="horarioMode === 'global'" class="flex flex-col gap-3">
        <WorkingHoursEditor v-model="globalWorkingHours" title="" helper="" />
        <div class="flex justify-end">
          <Button label="Salvar horário geral" :is-loading="isSaving" @click="saveGlobalHours" />
        </div>
      </div>

      <!-- Modo per-agent -->
      <div v-else class="flex flex-col gap-3">
        <div v-if="isLoadingAgents" class="text-body-small text-n-slate-11">Carregando vendedores…</div>
        <div v-else class="grid grid-cols-2 gap-4">
          <div class="border border-n-strong rounded-md overflow-hidden">
            <header class="px-3 py-2 text-body-small text-n-slate-12 bg-n-slate-3 border-b border-n-strong font-medium">
              Vendedores
            </header>
            <ul class="max-h-96 overflow-auto">
              <li v-for="a in agents" :key="a.id"
                  class="px-3 py-2 border-t border-n-strong text-body-small flex items-center justify-between cursor-pointer hover:bg-n-slate-2"
                  :class="selectedAgent?.id === a.id ? 'bg-n-blue-2' : ''"
                  @click="editAgentSchedule(a)">
                <div>
                  <div class="text-n-slate-12">{{ a.name }}</div>
                  <div class="text-n-slate-11 text-label-small">
                    {{ a.configured ? 'Horário próprio' : 'Usa o horário geral' }}
                  </div>
                </div>
                <button v-if="a.configured" class="text-n-ruby-9 text-label-small hover:underline"
                        @click.stop="resetAgentSchedule(a)">
                  Resetar
                </button>
              </li>
            </ul>
          </div>
          <div class="border border-n-strong rounded-md overflow-hidden">
            <header class="px-3 py-2 text-body-small text-n-slate-12 bg-n-slate-3 border-b border-n-strong font-medium">
              {{ selectedAgent ? `Horário de ${selectedAgent.name}` : 'Selecione um vendedor' }}
            </header>
            <div v-if="selectedAgent" class="p-3 flex flex-col gap-3">
              <WorkingHoursEditor v-model="editingAgentHours" title="" helper="" />
              <div class="flex justify-end gap-2">
                <Button label="Cancelar" variant="ghost" @click="selectedAgent = null" />
                <Button label="Salvar horário do vendedor" :is-loading="isSaving" @click="saveAgentSchedule" />
              </div>
            </div>
            <div v-else class="p-6 text-center text-body-small text-n-slate-11">
              Clique em um vendedor à esquerda para configurar sua escala.
            </div>
          </div>
        </div>
      </div>
    </section>
    </div>
  </div>
</template>
